import {
  useDeleteOrganization,
  useOrganization,
  useUpdateOrganization,
} from "@/api/organization";
import { DeleteOrganizationDialog } from "@/components/DeleteOrganizationDialog/DeleteOrganizationDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Organization } from "@cohesive/api-client";
import { Spinner } from "@cohesive/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as z from "zod";

const emojiRegex = /[\p{Emoji_Presentation}\p{Extended_Pictographic}]/u;

const formSchema = z.object({
  name: z
    .string()
    .min(2, {
      message: "Organization name must be at least 2 characters.",
    })
    .max(64, {
      message: "Organization name exceeds maximum length of 64 characters.",
    })
    .refine((val) => !emojiRegex.test(val), {
      message: "Organization name cannot contain emojis.",
    }),
  logo_url: z
    .string()
    .optional()
    .refine(
      (val) =>
        !val ||
        (val.startsWith("https://") && z.string().url().safeParse(val).success),
      {
        message: "Logo URL must be a valid HTTPS URL.",
      },
    ),
});

type FormData = z.infer<typeof formSchema>;

interface ImagePreviewProps {
  url: string;
  onError: (error: string) => void;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ url, onError }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleImageLoad = () => {
    setLoading(false);
    setError(null);
  };

  const handleImageError = () => {
    setLoading(false);
    const errorMsg =
      "Failed to load image. Please check the URL and try again.";
    setError(errorMsg);
    onError(errorMsg);
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
  }, [url]);

  if (!url) {
    return null;
  }

  // Validate URL before attempting to load
  const isValidUrl =
    url.startsWith("https://") && z.string().url().safeParse(url).success;

  if (!isValidUrl) {
    return (
      <div className="flex items-center justify-center w-20 h-20 bg-destructive/10 border border-destructive/20 rounded-lg">
        <AlertCircle className="h-6 w-6 text-destructive" />
      </div>
    );
  }

  return (
    <div className="relative">
      {loading && (
        <div className="flex items-center justify-center w-20 h-20 bg-muted rounded-lg">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      )}
      {error && (
        <div className="flex items-center justify-center w-20 h-20 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertCircle className="h-6 w-6 text-destructive" />
        </div>
      )}
      <img
        src={url}
        alt="Organization logo preview"
        className={`w-20 h-20 object-cover rounded-lg border ${
          loading || error ? "hidden" : ""
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );
};

export const OrgSettings: React.FC = () => {
  const { data: organization, isLoading } = useOrganization();
  const updateOrganization = useUpdateOrganization();
  const deleteOrganization = useDeleteOrganization();
  const [imageError, setImageError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      logo_url: "",
    },
  });

  const logoUrl = form.watch("logo_url");

  useEffect(() => {
    if (organization) {
      form.reset({
        name: organization.name || "",
        logo_url: organization.logo_url || "",
      });
    }
  }, [organization, form]);

  const handleSubmit = async (data: FormData) => {
    if (imageError) {
      toast.error("Please fix the image URL error before saving");
      return;
    }

    try {
      await updateOrganization.mutateAsync({
        name: data.name.trim(),
        logo_url: data.logo_url?.trim() || undefined,
      });

      toast.success("Organization updated successfully");
    } catch (error) {
      console.error("Failed to update organization:", error);
      toast.error("Failed to update organization");
    }
  };

  const handleImageError = (error: string) => {
    setImageError(error);
  };

  const handleLogoUrlChange = () => {
    setImageError(null);
  };

  const handleDeleteOrganization = async (_org: Organization) => {
    try {
      await deleteOrganization.mutateAsync();
      toast.success("Organization deleted successfully");
      // Note: User will be redirected automatically due to org deletion
    } catch (error) {
      console.error("Failed to delete organization:", error);
      toast.error("Failed to delete organization");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <title>Organization Settings</title>

      <div className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <Card>
              <CardHeader>
                <CardTitle>General Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Enter organization name"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="logo_url"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL</FormLabel>
                      <div className="flex gap-4 items-start">
                        <div className="flex-1">
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="https://example.com/logo.png"
                              onChange={(e) => {
                                field.onChange(e);
                                handleLogoUrlChange();
                              }}
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground mt-1">
                            Enter a URL to an image that will be used as your
                            organization's logo
                          </p>
                          <FormMessage />
                          {imageError && (
                            <p className="text-xs text-destructive mt-1">
                              {imageError}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {logoUrl ? (
                            <ImagePreview
                              url={logoUrl}
                              onError={handleImageError}
                            />
                          ) : (
                            <div className="flex items-center justify-center w-20 h-20 bg-muted border border-dashed border-muted-foreground/25 rounded-lg">
                              <p className="text-xs text-muted-foreground text-center p-1">
                                Preview
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </FormItem>
                  )}
                />

                <div className="flex justify-end pt-4">
                  <Button
                    type="submit"
                    isLoading={updateOrganization.isPending}
                    disabled={updateOrganization.isPending || !!imageError}
                  >
                    Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </form>
        </Form>

        {/* Danger Zone */}
        <Card className="border-destructive/20">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h4 className="text-sm font-medium">Delete Organization</h4>
                <p className="text-sm text-muted-foreground">
                  Permanently delete this organization and all associated data.
                  This action cannot be undone.
                </p>
              </div>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                disabled={!organization}
              >
                Delete Organization
              </Button>
            </div>
          </CardContent>
        </Card>

        <DeleteOrganizationDialog
          organization={showDeleteDialog ? organization || null : null}
          onConfirmDelete={handleDeleteOrganization}
          onCancel={() => setShowDeleteDialog(false)}
          isLoading={deleteOrganization.isPending}
        />
      </div>
    </>
  );
};

export default OrgSettings;
